/**
 * SchedulePreviewModal — Sprint 4.7.2
 *
 * Modal/drawer que mostra comparação visual da agenda ATUAL vs PROPOSTA
 * antes de salvar. Frontend-only — compara snapshot.schedule (lido do
 * backend) vs estado local do editor.
 *
 * Layout pedido no briefing:
 *
 *   ATUAL                    PROPOSTA
 *   Segunda
 *     04:30 reboot           04:30 reboot          ← cinza (unchanged)
 *                            06:00 relay_off       ← verde (added)
 *   Terça
 *     04:30 reboot           — removido —          ← vermelho (removed)
 *   Quarta
 *     — sem agenda —         04:30 reboot          ← verde (added)
 *
 *   Resumo: +2 entradas · -1 entrada · 0 alterada
 *
 * Cores:
 *   verde     = added
 *   amarelo   = changed
 *   vermelho  = removed
 *   cinza     = unchanged
 */
import { useMemo } from "react";
import { t } from "../../i18n/index.js";
import { diffScheduleFull } from "./scheduleDiff.js";
import { isPulseEntry } from "./ScheduleEditor.jsx";

// Mapeamento visual de status → classes Tailwind para cada célula
const STATUS_STYLE = {
  unchanged: {
    border: "border-noc-border/40",
    bg: "bg-noc-bg/20",
    text: "text-noc-muted",
    badge: "border-noc-border/60 text-noc-muted/80",
  },
  added: {
    border: "border-noc-ok/30",
    bg: "bg-noc-ok/10",
    text: "text-noc-ok",
    badge: "border-noc-ok/30 text-noc-ok",
  },
  changed: {
    border: "border-noc-warn/30",
    bg: "bg-noc-warn/10",
    text: "text-noc-warn",
    badge: "border-noc-warn/30 text-noc-warn",
  },
  removed: {
    border: "border-noc-err/30",
    bg: "bg-noc-err/10",
    text: "text-noc-err",
    badge: "border-noc-err/30 text-noc-err",
  },
};

function fmtEntry(entry) {
  if (!entry) return null;
  if (isPulseEntry(entry)) {
    return {
      sig: entry.time,
      duration: `${entry.duration_s ?? "?"}s`,
      action: entry.action || "relay_reboot",
      modeKey: "templateBuilder.pulseReboot",
    };
  }
  return {
    sig: `${entry.on_time} → ${entry.off_time}`,
    duration: null,
    action: entry.action || "relay_on",
    modeKey: "templateBuilder.onOffFixed",
  };
}

function EntryCell({ entry, status, side }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.unchanged;

  // Célula vazia: significa que o slot existe no OUTRO lado
  if (!entry) {
    // Side = "current" (atual) e ausente = era added na proposta
    // Side = "proposed" (proposta) e ausente = é removed
    const placeholderKey =
      side === "current"
        ? "schedulePreview.noScheduleHere"   // "— sem agenda aqui —"
        : "schedulePreview.removedSlot";     // "— removido —"
    return (
      <div className={`border rounded p-2 ${style.border} ${style.bg}`}>
        <div className="text-[10px] italic text-noc-muted/60">
          {t(placeholderKey)}
        </div>
      </div>
    );
  }

  const f = fmtEntry(entry);
  return (
    <div className={`border rounded p-2 ${style.border} ${style.bg}`}>
      <div className="flex items-center gap-2 text-[11px]">
        <span className={`font-mono font-semibold ${style.text}`}>{f.sig}</span>
        <span className={`text-[9px] font-bold px-1 py-0.5 rounded border ${style.badge}`}>
          {t(f.modeKey).replace(/^[+\s]+/, "")}
        </span>
        {entry.enabled === false && (
          <span className="text-[9px] px-1 py-0.5 rounded border border-noc-border/60 text-noc-muted/70">
            {t("schedulePreview.disabledBadge")}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-noc-muted mt-0.5 font-mono">
        <span>{f.action}</span>
        {f.duration && <span>· {f.duration}</span>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.unchanged;
  const label = t(`schedulePreview.status.${status}`);
  return (
    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${style.badge}`}>
      {label.toUpperCase()}
    </span>
  );
}

export default function SchedulePreviewModal({
  open,
  current,
  proposed,
  onClose,
  onCopyCurrent,
  onRestoreCurrent,
  onClearAll,
}) {
  // Hooks SEMPRE antes do early return — evita erro do React.
  const diff = useMemo(
    () => diffScheduleFull(current, proposed),
    [current, proposed]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2200] bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-4xl flex flex-col"
        style={{ maxHeight: "92vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <h3 className="text-sm font-semibold text-noc-accent">
            🔬 {t("schedulePreview.title")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Summary chips + ações de operação */}
        <div className="px-4 py-2 border-b border-noc-border flex flex-wrap items-center gap-2 text-[11px]">
          {diff.identical ? (
            <span className="text-noc-ok font-semibold">
              ✓ {t("schedulePreview.identical")}
            </span>
          ) : (
            <>
              {diff.enabledChanged && (
                <span className="px-1.5 py-0.5 rounded border border-noc-warn/30 text-noc-warn text-[10px]">
                  {diff.enabledTo
                    ? t("schedulePreview.enabledTurnedOn")
                    : t("schedulePreview.enabledTurnedOff")}
                </span>
              )}
              <span className="text-noc-ok">
                +{diff.summary.added} {t("schedulePreview.entries")}
              </span>
              <span className="text-noc-err">
                −{diff.summary.removed} {t("schedulePreview.entries")}
              </span>
              <span className="text-noc-warn">
                {diff.summary.changed} {t("schedulePreview.changed")}
              </span>
              <span className="text-noc-muted/70">
                {diff.summary.unchanged} {t("schedulePreview.unchanged")}
              </span>
              {(diff.exceptionsAdded.length > 0 ||
                diff.exceptionsRemoved.length > 0) && (
                <span className="text-noc-muted">
                  · {t("schedulePreview.exceptionsChange", {
                    added: diff.exceptionsAdded.length,
                    removed: diff.exceptionsRemoved.length,
                  })}
                </span>
              )}
            </>
          )}
          <div className="ml-auto flex items-center gap-1">
            {onCopyCurrent && (
              <button
                type="button"
                onClick={onCopyCurrent}
                className="text-[10px] px-2 py-1 rounded border border-noc-accent/30 text-noc-accent hover:bg-noc-accent/10"
                title={t("schedulePreview.copyCurrentHelp")}
              >
                📋 {t("schedulePreview.copyCurrent")}
              </button>
            )}
            {onRestoreCurrent && (
              <button
                type="button"
                onClick={onRestoreCurrent}
                className="text-[10px] px-2 py-1 rounded border border-noc-warn/30 text-noc-warn hover:bg-noc-warn/10"
                title={t("schedulePreview.restoreCurrentHelp")}
              >
                ↩ {t("schedulePreview.restoreCurrent")}
              </button>
            )}
            {onClearAll && (
              <button
                type="button"
                onClick={onClearAll}
                className="text-[10px] px-2 py-1 rounded border border-noc-err/30 text-noc-err hover:bg-noc-err/10"
                title={t("schedulePreview.clearAllHelp")}
              >
                🗑 {t("schedulePreview.clearAll")}
              </button>
            )}
          </div>
        </div>

        {/* Body — matriz Atual × Proposta agrupada por dia */}
        <div className="flex-1 overflow-y-auto p-4 text-xs">
          {diff.byDay.length === 0 ? (
            <div className="text-center py-12 text-noc-muted">
              <div className="text-2xl mb-2">📭</div>
              <div>{t("schedulePreview.bothEmpty")}</div>
            </div>
          ) : (
            <>
              {/* Headers das duas colunas */}
              <div className="grid grid-cols-[80px_1fr_1fr] gap-2 mb-2 text-[10px] uppercase text-noc-muted tracking-wide font-semibold">
                <div></div>
                <div>{t("schedulePreview.colCurrent")}</div>
                <div>{t("schedulePreview.colProposed")}</div>
              </div>

              {/* Uma seção por dia */}
              {diff.byDay.map(({ day, dayName, rows }) => (
                <div key={day} className="mb-3">
                  {/* Header do dia */}
                  <div className="grid grid-cols-[80px_1fr_1fr] gap-2 items-baseline mb-1">
                    <div className="text-noc-accent font-semibold text-[11px]">
                      {dayName}
                    </div>
                    <div className="border-b border-noc-border/30 col-span-2"></div>
                  </div>

                  {/* Rows do dia */}
                  <div className="space-y-1.5">
                    {rows.map((row, ridx) => (
                      <div
                        key={ridx}
                        className="grid grid-cols-[80px_1fr_1fr] gap-2 items-center"
                      >
                        <div className="text-[10px]">
                          <StatusBadge status={row.status} />
                        </div>
                        <EntryCell
                          entry={row.current}
                          status={row.status}
                          side="current"
                        />
                        <EntryCell
                          entry={row.proposed}
                          status={row.status}
                          side="proposed"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Exceptions diff (não tem dia da semana — bloco separado) */}
              {(diff.exceptionsAdded.length > 0 ||
                diff.exceptionsRemoved.length > 0) && (
                <div className="mt-4 pt-3 border-t border-noc-border">
                  <div className="text-noc-accent font-semibold text-[11px] mb-1.5">
                    {t("schedulePreview.exceptionsSectionTitle")}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <div className="text-[10px] text-noc-err uppercase tracking-wide font-semibold">
                        − {t("schedulePreview.exceptionsRemoved")}
                      </div>
                      {diff.exceptionsRemoved.length === 0 ? (
                        <div className="text-[10px] text-noc-muted/60 italic">
                          {t("common.none")}
                        </div>
                      ) : (
                        diff.exceptionsRemoved.map((d) => (
                          <span
                            key={d}
                            className="inline-block mr-1 text-[10px] font-mono px-2 py-0.5 rounded border border-noc-err/30 text-noc-err bg-noc-err/10"
                          >
                            {d}
                          </span>
                        ))
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="text-[10px] text-noc-ok uppercase tracking-wide font-semibold">
                        + {t("schedulePreview.exceptionsAdded")}
                      </div>
                      {diff.exceptionsAdded.length === 0 ? (
                        <div className="text-[10px] text-noc-muted/60 italic">
                          {t("common.none")}
                        </div>
                      ) : (
                        diff.exceptionsAdded.map((d) => (
                          <span
                            key={d}
                            className="inline-block mr-1 text-[10px] font-mono px-2 py-0.5 rounded border border-noc-ok/30 text-noc-ok bg-noc-ok/10"
                          >
                            {d}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-noc-border flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
          >
            {t("common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}
